import { useRef, useState } from 'react';
import { useUnsavedChanges } from '../../lib/use-unsaved-changes.js';

export default function RuleEditor({ editing, catalog, onChange, onSave, onCancel, busy = false }) {
  const [fixedEmailInput, setFixedEmailInput] = useState('');
  const [peopleInput, setPeopleInput] = useState('');
  const [fallbackInput, setFallbackInput] = useState('');
  // Mounted only while the dialog is open: what it opened with is the baseline. Save closes it (unmounts); Cancel too.
  const baseline = useRef(JSON.stringify(editing));
  useUnsavedChanges(JSON.stringify(editing) !== baseline.current || [fixedEmailInput, peopleInput, fallbackInput].some((v) => v.trim() !== ''));

  const selectedEvent = catalog.find((e) => e.id === editing.eventId) ?? null;

  function setEventId(eventId) {
    const evt = catalog.find((e) => e.id === eventId);
    const initParams = {};
    (evt?.params ?? []).forEach((p) => { initParams[p.key] = p.default; });
    // Strip group-owners and fallback recipients if switching to non-group-scoped event
    const recipients = editing.recipients.filter(
      (r) => (r.type !== 'group-owners' && r.type !== 'fallback') || (evt?.groupScoped ?? false)
    );
    onChange({ ...editing, eventId, params: initParams, recipients });
    setFallbackInput('');
  }

  function setParam(key, value) {
    onChange({ ...editing, params: { ...editing.params, [key]: value } });
  }

  function toggleRecipient(type, checked) {
    let recipients = editing.recipients.filter((r) => r.type !== type);
    if (checked) {
      if (type === 'default') recipients = [...recipients, { type: 'default' }];
      if (type === 'group-owners') recipients = [...recipients, { type: 'group-owners' }];
    }
    onChange({ ...editing, recipients });
  }

  function addFixedEmail() {
    const email = fixedEmailInput.trim();
    if (!email) return;
    onChange({ ...editing, recipients: [...editing.recipients, { type: 'fixed', email }] });
    setFixedEmailInput('');
  }

  function addPeople() {
    const emails = peopleInput
      .split(/[\s,]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (emails.length === 0) return;
    onChange({ ...editing, recipients: [...editing.recipients, { type: 'people', emails }] });
    setPeopleInput('');
  }

  function removeRecipient(index) {
    const recipients = editing.recipients.filter((_, i) => i !== index);
    onChange({ ...editing, recipients });
  }

  function handleSaveWithFallback() {
    let recipients = editing.recipients;
    const fb = fallbackInput.trim();
    if (fb && selectedEvent?.groupScoped) {
      // Remove any existing fallback then add new one
      recipients = recipients.filter((r) => r.type !== 'fallback');
      recipients = [...recipients, { type: 'fallback', email: fb }];
      onChange({ ...editing, recipients });
      // Defer actual save until state is flushed: we pass updated recipients directly
      onSave({ ...editing, recipients });
      return;
    }
    onSave();
  }

  const hasDefault = editing.recipients.some((r) => r.type === 'default');
  const hasGroupOwners = editing.recipients.some((r) => r.type === 'group-owners');

  return (
    <div className="border border-slate-200 rounded-lg p-4 space-y-4 bg-slate-50">
      <div>
        <label className="label" htmlFor="notif-event-select">Event</label>
        <select
          id="notif-event-select"
          aria-label="Event"
          className="input mt-1"
          value={editing.eventId}
          onChange={(e) => setEventId(e.target.value)}
        >
          <option value="">Select an event</option>
          {catalog.map((evt) => (
            <option key={evt.id} value={evt.id}>{evt.label}</option>
          ))}
        </select>
      </div>

      {/* Params */}
      {selectedEvent && selectedEvent.params.map((param) => (
        <div key={param.key}>
          <label className="label" htmlFor={`param-${param.key}`}>{param.label}</label>
          {param.type === 'number' ? (
            <input
              id={`param-${param.key}`}
              type="number"
              className="input mt-1"
              value={editing.params[param.key] ?? param.default}
              onChange={(e) => setParam(param.key, Number(e.target.value))}
            />
          ) : (
            <input
              id={`param-${param.key}`}
              type="text"
              className="input mt-1"
              placeholder="Comma-separated values"
              value={Array.isArray(editing.params[param.key]) ? editing.params[param.key].join(', ') : (editing.params[param.key] ?? '')}
              onChange={(e) => setParam(param.key, e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
            />
          )}
        </div>
      ))}

      {/* Recipients */}
      {selectedEvent && (
        <div className="space-y-3">
          <div className="label">Recipients</div>

          {/* Customer default checkbox */}
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={hasDefault}
              onChange={(e) => toggleRecipient('default', e.target.checked)}
            />
            Customer default
          </label>

          {/* Group-owners checkbox + fallback input (group-scoped only) */}
          {selectedEvent.groupScoped && (
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={hasGroupOwners}
                  onChange={(e) => toggleRecipient('group-owners', e.target.checked)}
                />
                Group owners
              </label>
              <div>
                <label className="label text-xs" htmlFor="notif-fallback-email">Fallback email</label>
                <input
                  id="notif-fallback-email"
                  type="email"
                  className="input mt-1"
                  placeholder="fallback@example.com"
                  value={fallbackInput}
                  onChange={(e) => setFallbackInput(e.target.value)}
                />
              </div>
            </div>
          )}

          {/* Fixed email input */}
          <div>
            <label className="label text-xs" htmlFor="notif-fixed-email">Fixed email</label>
            <div className="flex gap-2 mt-1">
              <input
                id="notif-fixed-email"
                type="email"
                className="input flex-1"
                placeholder="fixed@example.com"
                value={fixedEmailInput}
                onChange={(e) => setFixedEmailInput(e.target.value)}
              />
              <button
                type="button"
                className="btn-secondary text-sm shrink-0"
                onClick={addFixedEmail}
              >
                Add email
              </button>
            </div>
          </div>

          {/* Specific people input */}
          <div>
            <label className="label text-xs" htmlFor="notif-people-email">Specific people</label>
            <div className="flex gap-2 mt-1">
              <input
                id="notif-people-email"
                type="text"
                className="input flex-1"
                placeholder="a@example.com, b@example.com"
                value={peopleInput}
                onChange={(e) => setPeopleInput(e.target.value)}
              />
              <button
                type="button"
                className="btn-secondary text-sm shrink-0"
                onClick={addPeople}
              >
                Add people
              </button>
            </div>
          </div>

          {/* Current recipients list with remove controls */}
          {editing.recipients.length > 0 && (
            <ul className="space-y-1">
              {editing.recipients.map((r, i) => (
                <li key={i} className="flex items-center justify-between text-sm bg-white border border-slate-200 rounded px-3 py-1">
                  <span>
                    {r.type === 'default' && 'Customer default'}
                    {r.type === 'group-owners' && 'Group owners'}
                    {r.type === 'fixed' && r.email}
                    {r.type === 'people' && (r.emails || []).join(', ')}
                    {r.type === 'fallback' && `Fallback: ${r.email}`}
                  </span>
                  <button
                    type="button"
                    className="text-ipc-muted hover:text-red-500 ml-2"
                    aria-label={`Remove recipient ${i}`}
                    onClick={() => removeRecipient(i)}
                  >
                    ✕
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="flex gap-2">
        <button className="btn-primary text-sm" onClick={handleSaveWithFallback} disabled={busy}>Save rule</button>
        <button className="btn-secondary text-sm" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}
